import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

const API_BASE = import.meta.env.VITE_API_URL || ''
const QUERY_KEY = 'company-logo'

/**
 * Object URL of the logo served by the backend proxy, or null when the domain
 * has none (204). Any other status (a transient 502 from Google, a 401 while
 * the session refreshes, ...) throws: the outcome is unknown, so it is not
 * cached as "no logo" and the next mount asks again. The session refresh
 * itself is left to the regular API calls.
 */
async function fetchCompanyLogo(domain: string): Promise<string | null> {
  const token = localStorage.getItem('access_token')
  const response = await fetch(`${API_BASE}/api/company-logos?domain=${encodeURIComponent(domain)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (response.status === 204) return null
  if (response.status !== 200) throw new Error(`Company logo request failed with ${response.status}`)
  return URL.createObjectURL(await response.blob())
}

// Logos live in the query cache for the whole session, so their object URLs
// are released when the cache drops them (e.g. queryClient.clear() on logout),
// not when a row unmounts: other rows may still show the same URL.
const clientsRevokingLogos = new WeakSet<QueryClient>()

function revokeLogoUrlsOnRemoval(queryClient: QueryClient) {
  if (clientsRevokingLogos.has(queryClient)) return
  clientsRevokingLogos.add(queryClient)
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'removed' || event.query.queryKey[0] !== QUERY_KEY) return
    const url = event.query.state.data
    if (typeof url === 'string') URL.revokeObjectURL(url)
  })
}

/**
 * Company logo for a domain, fetched only when `enabled` (e.g. once visible).
 * Each domain is requested once per session: rows sharing a company reuse it.
 */
export function useCompanyLogo(domain: string | null, enabled: boolean) {
  const queryClient = useQueryClient()
  revokeLogoUrlsOnRemoval(queryClient)
  return useQuery({
    queryKey: [QUERY_KEY, domain],
    queryFn: () => fetchCompanyLogo(domain as string),
    enabled: enabled && domain !== null,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })
}
